import store from '../store'
import router from '../routes'
import { logUserAction } from './logging'

function getPreLoginAdConfig() {
  return store.state.baseData?.advertisement?.preLogin ?? {}
}

function getInterstitialConfig() {
  return store.state.baseData?.advertisement?.interstitial ?? {}
}

function hasPreLoginAd() {
  const flags = store.state.baseData?.flags || {}
  const adConfig = getPreLoginAdConfig()
  return Boolean(flags.isAdvertising && flags.adsPreLogin && adConfig.integrationId)
}

function hasInterstitialAd() {
  const flags = store.state.baseData?.flags || {}
  return Boolean(flags.isAdvertising && flags.adsInterstitial && getInterstitialConfig().top?.id)
}

function getConditionalNextPage() {
  const flags = store.state.baseData?.flags || {}

  if (flags.isCovid) {
    return 'covid'
  } else if (flags.isPaidCampaign) {
    return 'payment-plans'
  } else {
    return 'social-logins'
  }
}

function getAfterPreLoginAdNext() {
  const flags = store.state.baseData?.flags || {}

  if (flags.isConsent) {
    return 'consent'
  }
  return getConditionalNextPage()
}

const PAGE_SCHEMA = {
  terms: {
    name: 'TermsPage',
    path: '/',
    required: true,
    getNext: () => {
      if (hasPreLoginAd()) {
        return 'pre-login-ad'
      }

      const flags = store.state.baseData.flags || {}
      if (flags.isConsent) {
        return 'consent'
      }
      return getConditionalNextPage()
    },
  },

  'pre-login-ad': {
    name: 'PreLoginAd',
    path: '/pre-login-ad',
    required: false,
    getNext: () => getAfterPreLoginAdNext(),
  },

  consent: {
    name: 'ConsentForm',
    path: '/consent',
    required: false,
    getNext: () => getConditionalNextPage(),
  },

  covid: {
    name: 'Covid',
    path: '/covid',
    required: false,
    getNext: () => {
      const flags = store.state.baseData.flags || {}
      return flags.isPaidCampaign ? 'payment-plans' : 'social-logins'
    },
  },

  'payment-plans': {
    name: 'PaymentPlans',
    path: '/payment-plans',
    required: false,
    getNext: () => 'social-logins',
  },

  'social-logins': {
    name: 'Logins',
    path: '/social-logins',
    required: true,
    getNext: () => {
      const nextPage = 'thankyou'
      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] social-logins.getNext()',
        data: {
          currentPage: 'social-logins',
          nextPage,
          reason: 'Post-login flow - always goes to thankyou after backend response',
        },
      })
      return nextPage
    },
  },

  thankyou: {
    name: 'thankyou',
    path: '/thankyou',
    required: true,
    getNext: () => {
      const flags = store.state.baseData.flags || {}
      const hasInterstitial = hasInterstitialAd()
      const interstitialShown =
        typeof window !== 'undefined'
          ? sessionStorage.getItem('interstitialShown') === 'true'
          : false

      const decisionData = {
        currentPage: 'thankyou',
        flags: {
          isAdvertising: flags.isAdvertising,
          adsInterstitial: flags.adsInterstitial,
        },
        hasInterstitialAd: hasInterstitial,
        interstitialShown,
        interstitialAdId: getInterstitialConfig().top?.id,
      }

      if (hasInterstitial && !interstitialShown) {
        logUserAction({
          type: 'navigation',
          label: '[SCHEMA] thankyou.getNext() → interstitial',
          data: {
            ...decisionData,
            nextPage: 'interstitial',
            reason: 'Interstitial ad configured and not yet shown',
          },
        })
        return 'interstitial'
      }

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] thankyou.getNext() → END',
        data: {
          ...decisionData,
          nextPage: null,
          reason:
            hasInterstitial && interstitialShown
              ? 'Interstitial already shown'
              : !hasInterstitial
              ? 'No interstitial ad configured'
              : 'End of flow',
        },
      })
      return null
    },
  },

  interstitial: {
    name: 'interstitial',
    path: '/interstitial',
    required: false,
    getNext: () => {
      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] interstitial.getNext() → thankyou',
        data: {
          currentPage: 'interstitial',
          nextPage: 'thankyou',
          reason: 'Interstitial completed, returning to thankyou (final)',
        },
      })
      return 'thankyou'
    },
  },

  'contract-failed': {
    name: 'ContractFailed',
    path: '/contract-failed',
    required: false,
    getNext: () => null,
  },

  'retry-connection': {
    name: 'RetryConnection',
    path: '/retry-connection',
    required: false,
    getNext: () => null,
  },
}

const navigationHistory = []
let isNavigating = false

function getPageIdFromPath(path) {
  const entry = Object.entries(PAGE_SCHEMA).find(([_, config]) => config.path === path)
  return entry ? entry[0] : null
}

function getCurrentPageId() {
  return getPageIdFromPath(router.currentRoute.path)
}

const screens = {
  async advance() {
    if (isNavigating) {
      logUserAction({
        type: 'warning',
        label: '[SCHEMA] screens.advance() - Navigation already in progress, ignoring request',
        data: { timestamp: new Date().toISOString() },
      })
      return
    }

    isNavigating = true
    try {
      const currentPath = router.currentRoute.path
      const currentPageId = getCurrentPageId()

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] screens.advance() called',
        data: {
          currentPath,
          currentPageId,
          timestamp: new Date().toISOString(),
        },
      })

      if (!currentPageId) {
        const warning = 'screens.advance(): Could not determine current page'
        logUserAction({
          type: 'error',
          label: '[SCHEMA] screens.advance() failed',
          data: { error: warning, currentPath },
        })
        return
      }

      const currentPage = PAGE_SCHEMA[currentPageId]
      if (!currentPage) {
        const warning = `screens.advance(): Unknown page ID: ${currentPageId}`
        logUserAction({
          type: 'error',
          label: '[SCHEMA] screens.advance() failed',
          data: { error: warning, currentPageId },
        })
        return
      }

      const nextPageId = currentPage.getNext()

      if (!nextPageId) {
        logUserAction({
          type: 'navigation',
          label: '[SCHEMA] screens.advance() - End of flow',
          data: { currentPageId, nextPageId: null },
        })
        return
      }

      const nextPage = PAGE_SCHEMA[nextPageId]
      if (!nextPage) {
        const warning = `screens.advance(): Unknown next page ID: ${nextPageId}`
        logUserAction({
          type: 'error',
          label: '[SCHEMA] screens.advance() failed',
          data: { error: warning, currentPageId, nextPageId },
        })
        return
      }

      if (nextPageId !== currentPageId) {
        navigationHistory.push(currentPageId)
      }

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] screens.advance() navigating',
        data: {
          from: { id: currentPageId, path: currentPage.path, name: currentPage.name },
          to: { id: nextPageId, path: nextPage.path, name: nextPage.name },
          historyLength: navigationHistory.length,
        },
      })

      try {
        await router.push({
          path: nextPage.path,
          query: {},
        })

        if (typeof window !== 'undefined') {
          const cleanUrl = window.location.origin + window.location.pathname + '#' + nextPage.path
          window.history.replaceState({ path: nextPage.path }, document.title, cleanUrl)
        }
        logUserAction({
          type: 'navigation',
          label: '[SCHEMA] screens.advance() completed',
          data: {
            from: currentPageId,
            to: nextPageId,
            success: true,
          },
        })
      } catch (error) {
        if (error.name !== 'NavigationDuplicated') {
          logUserAction({
            type: 'error',
            label: '[SCHEMA] screens.advance() navigation error',
            data: {
              error: error.message,
              from: currentPageId,
              to: nextPageId,
            },
          })
          throw error
        }
      }
    } finally {
      isNavigating = false
    }
  },

  async back() {
    if (isNavigating) {
      logUserAction({
        type: 'warning',
        label: '[SCHEMA] screens.back() - Navigation already in progress, ignoring request',
        data: { timestamp: new Date().toISOString() },
      })
      return
    }

    isNavigating = true
    try {
      const currentPath = router.currentRoute.path
      const currentPageId = getCurrentPageId()

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] screens.back() called',
        data: {
          currentPath,
          currentPageId,
          historyLength: navigationHistory.length,
        },
      })

      if (navigationHistory.length === 0) {
        logUserAction({
          type: 'navigation',
          label: '[SCHEMA] screens.back() - No history',
          data: { error: 'screens.back(): No history to go back to', currentPageId },
        })
        return
      }

      const previousPageId = navigationHistory.pop()
      const previousPage = PAGE_SCHEMA[previousPageId]

      if (!previousPage) {
        const warning = `screens.back(): Unknown page ID: ${previousPageId}`
        logUserAction({
          type: 'error',
          label: '[SCHEMA] screens.back() failed',
          data: { error: warning, previousPageId },
        })
        return
      }

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] screens.back() navigating',
        data: {
          from: { id: currentPageId, path: currentPath },
          to: { id: previousPageId, path: previousPage.path, name: previousPage.name },
        },
      })

      try {
        await router.push({
          path: previousPage.path,
          query: {},
        })

        if (typeof window !== 'undefined') {
          const cleanUrl =
            window.location.origin + window.location.pathname + '#' + previousPage.path
          window.history.replaceState({ path: previousPage.path }, document.title, cleanUrl)
        }
      } catch (error) {
        if (error.name !== 'NavigationDuplicated') {
          logUserAction({
            type: 'error',
            label: '[SCHEMA] screens.back() navigation error',
            data: { error: error.message, from: currentPageId, to: previousPageId },
          })
          throw error
        }
      }
    } finally {
      isNavigating = false
    }
  },

  getCurrent() {
    const currentPageId = getCurrentPageId()
    const currentPage = PAGE_SCHEMA[currentPageId]

    return currentPage
      ? {
          id: currentPageId,
          name: currentPage.name,
          path: currentPage.path,
          required: currentPage.required,
        }
      : null
  },

  getNext() {
    const currentPageId = getCurrentPageId()
    if (!currentPageId) return null

    const currentPage = PAGE_SCHEMA[currentPageId]
    if (!currentPage) return null

    const nextPageId = currentPage.getNext()
    if (!nextPageId) return null

    const nextPage = PAGE_SCHEMA[nextPageId]
    if (!nextPage) return null

    return {
      id: nextPageId,
      name: nextPage.name,
      path: nextPage.path,
      required: nextPage.required,
    }
  },

  getHistory() {
    return [...navigationHistory]
  },

  getSchema() {
    const schema = {}
    Object.keys(PAGE_SCHEMA).forEach((pageId) => {
      const page = PAGE_SCHEMA[pageId]
      schema[pageId] = {
        name: page.name,
        path: page.path,
        required: page.required,
      }
    })
    return schema
  },

  isRequired(pageId) {
    const page = PAGE_SCHEMA[pageId]
    return page ? page.required : false
  },

  getFlow() {
    const flow = []
    let currentPageId = 'terms'

    while (currentPageId) {
      const currentPage = PAGE_SCHEMA[currentPageId]
      if (!currentPage) break

      flow.push(currentPageId)
      currentPageId = currentPage.getNext()
    }

    return flow
  },

  async navigateTo(pageId, query = {}) {
    if (isNavigating) {
      logUserAction({
        type: 'warning',
        label: '[SCHEMA] screens.navigateTo() - Navigation already in progress, ignoring request',
        data: { pageId, timestamp: new Date().toISOString() },
      })
      return
    }

    isNavigating = true
    try {
      const targetPage = PAGE_SCHEMA[pageId]
      if (!targetPage) {
        logUserAction({
          type: 'error',
          label: '[SCHEMA] screens.navigateTo() failed',
          data: { error: `screens.navigateTo(): Unknown page ID: ${pageId}`, pageId },
        })
        return
      }

      const currentPageId = getCurrentPageId()

      logUserAction({
        type: 'navigation',
        label: '[SCHEMA] screens.navigateTo() called',
        data: {
          from: currentPageId,
          to: pageId,
          path: targetPage.path,
          query,
          reason: 'Direct navigation to specific page',
        },
      })

      if (currentPageId && currentPageId !== pageId) {
        navigationHistory.push(currentPageId)
      }

      try {
        await router.push({
          path: targetPage.path,
          query,
        })

        if (typeof window !== 'undefined') {
          let cleanUrl = window.location.origin + window.location.pathname + '#' + targetPage.path
          if (Object.keys(query).length > 0) {
            const queryString = new URLSearchParams(query).toString()
            cleanUrl += '?' + queryString
          }
          window.history.replaceState({ path: targetPage.path, query }, document.title, cleanUrl)
        }

        logUserAction({
          type: 'navigation',
          label: '[SCHEMA] screens.navigateTo() completed',
          data: {
            from: currentPageId,
            to: pageId,
            success: true,
          },
        })
      } catch (error) {
        if (error.name !== 'NavigationDuplicated') {
          logUserAction({
            type: 'error',
            label: '[SCHEMA] screens.navigateTo() navigation error',
            data: {
              error: error.message,
              from: currentPageId,
              to: pageId,
            },
          })
          throw error
        }
      }
    } finally {
      isNavigating = false
    }
  },
}

if (typeof window !== 'undefined') {
  window.screens = new Proxy(screens, {
    set(target, prop, value) {
      return false
    },
    defineProperty(target, prop, descriptor) {
      return false
    },
    deleteProperty(target, prop) {
      return false
    },
  })
}

export default screens
